// In-chat "running low on credits" card — pure helpers shared by the chat state (use-chat), the BFF
// history mapping, and the card UI (MessageRow/CreditsCard). The wire shape is the harness contract:
// an ASSISTANT reply whose account is in the red credits band carries `metadata.creditsCard`, and the
// card rides the reply the way `connectorCard` does — the assistant still answers, the nudge sits
// beneath the answer.
//
// Deliberately dependency-free (mirrors lib/connector-card.ts): unit tests must not drag the
// common-lib graph, so the validation here MIRRORS common-lib's `readCreditsCard` — including the
// positive-cap rule and the string coercion — rather than importing it. Keep the two in step.

/**
 * WHICH credits nudge the server sent. `low` is the red-band card; `renewed` is the one that greets a
 * user whose cycle rolled over after they ran out and waited rather than upgrading.
 *
 * An unrecognized value reads as `low`, and so does a missing one. This client ships on its own train
 * and will meet servers both older and newer than itself: an unknown variant means the server knows
 * about a card this build does not, and rendering the card we DO have is strictly better than
 * rendering nothing at a moment the server judged worth interrupting.
 */
export type CreditsCardVariant = 'low' | 'renewed';

export type CreditsCardData = {
  /** Credits SPENT and the cap — the card draws the same usage bar the sidebar already draws. */
  used: number;
  cap: number;
  /** What just went right, ALREADY LOCALIZED by the server. The client never composes this sentence. */
  successLine?: string;
  /** Always resolved — see {@link CreditsCardVariant} for why this never surfaces as undefined. */
  variant: CreditsCardVariant;
};

/**
 * Read + sanitize `metadata.creditsCard` off a raw message entity.
 *
 * Fail-closed on a non-positive `cap`: a card that cannot draw a meter renders nothing rather than a
 * bar pinned at zero, which would read as "you have no credits" — the opposite of what this card says.
 * Numbers are coerced from strings because harness metadata is often a `map<string,string>`.
 */
export function readCreditsCard(metadata: unknown): CreditsCardData | undefined {
  const raw = (metadata as { creditsCard?: unknown } | undefined)?.creditsCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const { used, cap, successLine, variant } = raw as { used?: unknown; cap?: unknown; successLine?: unknown; variant?: unknown };

  const toInt = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Math.trunc(Number(value));
    return undefined;
  };

  const capInt = toInt(cap);
  const usedInt = toInt(used);
  if (capInt === undefined || capInt <= 0 || usedInt === undefined) return undefined;

  return {
    used: Math.max(0, usedInt),
    cap: capInt,
    ...(typeof successLine === 'string' && successLine.trim() ? { successLine } : {}),
    variant: variant === 'renewed' ? 'renewed' : 'low',
  };
}

/** Credits SPENT over the cap, 0–1 — exactly what the usage bar renders. */
export function creditsCardFraction(card: CreditsCardData): number {
  return card.cap > 0 ? Math.min(1, Math.max(0, card.used / card.cap)) : 0;
}
